import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { NOTIFICATION_TYPE } from '../../../constants/common'
import { SINGLE_EMAIL_ENTITY } from '../../../constants/entity'
import { ScreenContainerRef } from '../../../context'
import {
  useRegisterReload,
  useReloadCountersAfterRefetch,
} from '../../../hooks'
import { getQaSelector } from '../../../utils/common'
import Button from '../../Button'
import {
  EMAIL_ITEM_ID_KEY,
  EmailTableLayout,
  useEmailTablePage,
} from '../../Email'
import { NotificationInline } from '../../Notification'
import { PageActions, PageLoader } from '../../Page'
import { getEmptyStateActions, PageEmptyState } from '../../PageEmptyState'
import { TablePagination } from '../../TablePagination'
import { updateEntityTabTableConfig } from '../EntityTab.api'

import { getEntityEmailArchiveListApi } from './EntityTabEmails.api'
import {
  ENTITY_TAB_EMAILS_ADD_BUTTON_QA_NAME,
  ENTITY_TAB_EMAILS_DEFAULT_QA_NAME,
  ENTITY_TAB_EMAILS_STATE_KEYS,
} from './EntityTabEmails.constants'
import {
  useEntityTabEmailsActions,
  useEntityTabEmailsInitialMessageModal,
} from './EntityTabEmails.hooks'
import { getEntityTabEmailsItemActions } from './EntityTabEmails.utils'
import { EntityTabEmailsMessageModal } from './EntityTabEmailsMessageModal'

import * as styles from './EntityTabEmails.module.css'

import EmailIcon from '../../../icons/email.svg'
import PlusIcon from '../../../icons/plus.svg'

function EntityTabEmails({
  countApi,
  entity = SINGLE_EMAIL_ENTITY,
  qaName = ENTITY_TAB_EMAILS_DEFAULT_QA_NAME,
  scopeId,
  tableConfigApi,
  onClickAdd,
  onRegisterReload,
  onReloadCounters,
}) {
  const { t } = useTranslation()

  const [modal, setModal] = useState(null)

  function handleCloseModal() {
    setModal(null)
  }

  const listApi = getEntityEmailArchiveListApi(scopeId)

  const asyncTableOptions = {
    ...(countApi ? { countApi } : null),
    ...(tableConfigApi ? { tableConfigApi } : null),
    // Archive list has no filter-state endpoint, so keep filter state local.
    isLocalFilterState: true,
    itemIdKey: EMAIL_ITEM_ID_KEY,
    listApi,
    withoutSubMenu: true,
  }

  async function handleApplyTableConfig(data) {
    return updateEntityTabTableConfig(data, entity.plural)
  }

  const tablePage = useEmailTablePage({
    asyncTableOptions,
    entity,
    extraSelectionResetDeps: [scopeId],
    withBrowserTabTitle: false,
    onApplyTableConfig: handleApplyTableConfig,
  })

  const {
    handleCleanSelection,
    handleLoadInit,
    handleReload,
    handleSetPagination,
    isInitialLoadSettled,
    isLoading,
    isRefetching,
    items,
    pageScrollWrapperRef,
    pagination,
    serverError,
  } = tablePage

  useEffect(() => {
    if (scopeId) {
      handleLoadInit()
    }
  }, [scopeId])

  async function handleReloadAll() {
    handleCleanSelection()

    return handleReload()
  }

  const { handleReloadWithoutCounters } = useReloadCountersAfterRefetch({
    isRefetching,
    reload: handleReloadAll,
    onReloadCounters,
  })

  useRegisterReload({
    isReady: isInitialLoadSettled,
    reload: handleReloadWithoutCounters,
    onRegisterReload,
  })

  const { setItemAction } = useEntityTabEmailsActions({
    onCloseModal: handleCloseModal,
    onReload: handleReloadAll,
    onSetModal: setModal,
  })

  const hasEmails = items?.items?.length > 0
  const hasError = Boolean(serverError)
  const showEmptyState = !hasEmails && !isLoading

  const addButtonText = t('Emails.AddNewEmailBtnKey')

  const emptyStateActions = getEmptyStateActions({
    actionName: ENTITY_TAB_EMAILS_ADD_BUTTON_QA_NAME,
    isLoading,
    qaName: ENTITY_TAB_EMAILS_ADD_BUTTON_QA_NAME,
    shouldShowActions: Boolean(onClickAdd) && !hasError,
    text: addButtonText,
    onClick: onClickAdd,
  })

  function handleRowClick(uuid) {
    setModal(
      <EntityTabEmailsMessageModal
        messageUuid={uuid}
        scopeId={scopeId}
        onClose={handleCloseModal}
        onReload={handleReloadAll}
      />
    )
  }

  useEntityTabEmailsInitialMessageModal({
    scopeId,
    onOpenMessage: handleRowClick,
  })

  return (
    <ScreenContainerRef value={pageScrollWrapperRef}>
      <div
        {...getQaSelector({ value: qaName })}
        className={styles.EntityTabEmails}
        ref={pageScrollWrapperRef}
      >
        {isRefetching ? <PageLoader /> : null}
        <NotificationInline
          className={styles.notice}
          text={t('EntityTabEmails.CopiesNoticeKey')}
          type={NOTIFICATION_TYPE.info}
        />
        {onClickAdd && !showEmptyState ? (
          <PageActions className={styles.actions} isLoading={isLoading}>
            <Button.Text
              beforeIconProps={{ Icon: PlusIcon }}
              qaName={ENTITY_TAB_EMAILS_ADD_BUTTON_QA_NAME}
              text={addButtonText}
              onClick={onClickAdd}
            />
          </PageActions>
        ) : null}
        <EmailTableLayout
          emptyState={
            <PageEmptyState
              EntityIcon={EmailIcon}
              actions={emptyStateActions}
              text={t(
                hasError
                  ? ENTITY_TAB_EMAILS_STATE_KEYS.ErrorStateKey
                  : ENTITY_TAB_EMAILS_STATE_KEYS.EmptyStateKey
              )}
            />
          }
          getItemActions={getEntityTabEmailsItemActions}
          hasTable={!showEmptyState}
          modal={modal}
          rowExtraProps={{ onClick: handleRowClick }}
          setItemAction={setItemAction}
          tablePage={tablePage}
          withRowActions={false}
        />
        <TablePagination
          responsive
          isSkeleton={isLoading}
          value={pagination}
          onChange={handleSetPagination}
        />
      </div>
    </ScreenContainerRef>
  )
}

export default EntityTabEmails
